import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { 
  Bell, 
  Shield, 
  Save,
  Clock,
  Battery,
  Smartphone,
  Sliders,
  Check
} from 'lucide-react';

interface NotificationSetting {
  type: string;
  description: string;
  enabled: boolean;
}

interface EnergyThreshold {
  type: string;
  value: number;
  description: string;
}

export function AdminExport() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [notificationSettings, setNotificationSettings] = useState<NotificationSetting[]>([
    {
      type: 'energy_low',
      description: 'Alertes de baisse d\'énergie',
      enabled: true
    },
    {
      type: 'sleep_cycle',
      description: 'Notifications de cycle de sommeil',
      enabled: true
    },
    {
      type: 'inactivity',
      description: 'Alertes d\'inactivité',
      enabled: false
    }
  ]);

  const [thresholds, setThresholds] = useState<EnergyThreshold[]>([
    {
      type: 'critical',
      value: 20,
      description: 'Niveau d\'énergie critique'
    },
    {
      type: 'warning',
      value: 40,
      description: 'Niveau d\'énergie faible'
    },
    {
      type: 'optimal',
      value: 80,
      description: 'Niveau d\'énergie optimal'
    }
  ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simuler la sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Paramètres sauvegardés",
        description: "Les modifications ont été enregistrées avec succès.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleNotification = (type: string) => {
    setNotificationSettings(prev =>
      prev.map(setting =>
        setting.type === type
          ? { ...setting, enabled: !setting.enabled }
          : setting
      )
    );
  };

  const updateThreshold = (type: string, value: number) => {
    setThresholds(prev =>
      prev.map(threshold =>
        threshold.type === type
          ? { ...threshold, value }
          : threshold
      )
    );
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Paramètres des notifications
          </h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {notificationSettings.map((setting) => (
              <div
                key={setting.type}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium">{setting.description}</p>
                </div>
                <Button
                  variant={setting.enabled ? "default" : "outline"}
                  onClick={() => toggleNotification(setting.type)}
                  className="gap-2"
                >
                  {setting.enabled ? (
                    <>
                      <Check className="w-4 h-4" />
                      Activé
                    </>
                  ) : (
                    'Désactivé'
                  )}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Seuils d'énergie */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Battery className="w-5 h-5 text-blue-600" />
            Seuils d'énergie
          </h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {thresholds.map((threshold) => (
              <div
                key={threshold.type}
                className="space-y-2"
              >
                <div className="flex items-center justify-between">
                  <label className="font-medium">{threshold.description}</label>
                  <span className="text-sm text-gray-500">{threshold.value}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={threshold.value}
                  onChange={(e) => updateThreshold(threshold.type, parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Paramètres système */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sliders className="w-5 h-5 text-blue-600" />
            Paramètres système
          </h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <h4 className="font-medium">Intervalle de synchronisation</h4>
                </div>
                <select className="w-full border rounded-md p-2">
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="w-4 h-4 text-gray-600" />
                  <h4 className="font-medium">Limite d'appareils par utilisateur</h4>
                </div>
                <input
                  type="number"
                  min="1"
                  max="10"
                  defaultValue="3"
                  className="w-full border rounded-md p-2"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bouton de sauvegarde */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </Button>
      </div>
    </div>
  );
}